/**
 * ZonesAdminPage — visual map of the 40 delivery zones (20 Ottawa + 20 Ibadan)
 * rendered as SVG rectangles. Admin can click any zone to rename or recolor it.
 *
 * We use pure SVG (no map lib) — bboxes are so small that a straight
 * bbox → SVG-rect projection is more readable than a full tile map, and it
 * keeps the bundle tiny.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Map as MapIcon, Palette, Save, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../services/api';
import { toast } from 'sonner';

const COLOR_SWATCHES = [
  '#EF4444', '#F97316', '#F59E0B', '#EAB308', '#84CC16',
  '#22C55E', '#10B981', '#14B8A6', '#06B6D4', '#0EA5E9',
  '#3B82F6', '#6366F1', '#8B5CF6', '#A855F7', '#D946EF',
  '#EC4899', '#F43F5E', '#78716C', '#0F766E', '#0369A1',
];

const ZonesAdminPage = () => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('Ottawa');
  const [edit, setEdit] = useState(null); // { zone, name, color, saving }

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get('/zones').then((r) => r.data);
      setZones(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load zones');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const cityZones = useMemo(() => zones.filter((z) => z.city === tab), [zones, tab]);

  // Compute a common SVG viewBox per city so all bboxes share one canvas.
  const viewBox = useMemo(() => {
    if (!cityZones.length) return { x: 0, y: 0, w: 1, h: 1 };
    const mnla = Math.min(...cityZones.map((z) => z.bbox.min_lat));
    const mxla = Math.max(...cityZones.map((z) => z.bbox.max_lat));
    const mnlo = Math.min(...cityZones.map((z) => z.bbox.min_lng));
    const mxlo = Math.max(...cityZones.map((z) => z.bbox.max_lng));
    return { minLat: mnla, maxLat: mxla, minLng: mnlo, maxLng: mxlo };
  }, [cityZones]);

  // Lat→y is inverted (north is up).
  const SVG_W = 900;
  const SVG_H = 500;
  const project = (lat, lng) => {
    const spanLat = (viewBox.maxLat - viewBox.minLat) || 1;
    const spanLng = (viewBox.maxLng - viewBox.minLng) || 1;
    // Preserve aspect ratio.
    const aspect = (spanLng / spanLat) * Math.cos((viewBox.minLat + viewBox.maxLat) / 2 * Math.PI / 180);
    const drawW = Math.min(SVG_W, SVG_H * aspect);
    const drawH = Math.min(SVG_H, SVG_W / aspect);
    const offsetX = (SVG_W - drawW) / 2;
    const offsetY = (SVG_H - drawH) / 2;
    const x = offsetX + ((lng - viewBox.minLng) / spanLng) * drawW;
    const y = offsetY + (1 - (lat - viewBox.minLat) / spanLat) * drawH;
    return { x, y };
  };

  const save = async () => {
    if (!edit) return;
    setEdit((s) => ({ ...s, saving: true }));
    try {
      const r = await api.patch(`/admin/zones/${edit.zone.id}`, {
        name: edit.name.trim() || null,
        color: edit.color || null,
      });
      setZones((prev) => prev.map((z) => (z.id === edit.zone.id ? r.data : z)));
      toast.success(`Updated ${r.data.name}`);
      setEdit(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
      setEdit((s) => ({ ...s, saving: false }));
    }
  };

  const reseed = async () => {
    if (!window.confirm('Reseed all zones back to the default 40? This overwrites your renames + recolors.')) return;
    try {
      await api.post('/admin/zones/reseed');
      toast.success('Zones reseeded');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reseed failed');
    }
  };

  if (loading) {
    return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  }

  return (
    <div data-testid="admin-zones-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <MapIcon className="h-5 w-5 text-emerald-600" /> Delivery zones
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            ShipitNow ships to <b>Ottawa</b> and <b>Ibadan</b> only. Vendors and drivers are matched inside the same zone. Click any zone to rename it or change its color.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full bg-slate-100 p-0.5" role="tablist">
            {['Ottawa', 'Ibadan'].map((c) => (
              <button
                key={c}
                onClick={() => setTab(c)}
                className={`px-4 h-8 rounded-full text-xs font-semibold ${tab === c ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-800'}`}
                data-testid={`zones-tab-${c.toLowerCase()}`}
              >
                {c} · {zones.filter((z) => z.city === c).length}
              </button>
            ))}
          </div>
          <Button variant="outline" onClick={reseed} data-testid="zones-reseed-btn"><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Reseed</Button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-4">
          <svg viewBox={`0 0 ${SVG_W} ${SVG_H}`} className="w-full h-auto" data-testid={`zones-map-${tab.toLowerCase()}`}>
            <rect x="0" y="0" width={SVG_W} height={SVG_H} fill="#F8FAFC" rx="12" />
            {cityZones.map((z) => {
              const nw = project(z.bbox.max_lat, z.bbox.min_lng);
              const se = project(z.bbox.min_lat, z.bbox.max_lng);
              const w = Math.abs(se.x - nw.x);
              const h = Math.abs(se.y - nw.y);
              return (
                <g key={z.id} onClick={() => setEdit({ zone: z, name: z.name, color: z.color, saving: false })}
                   className="cursor-pointer" data-testid={`zone-rect-${z.id}`}>
                  <rect x={nw.x} y={nw.y} width={w} height={h} fill={z.color} opacity="0.55"
                        stroke={z.color} strokeWidth="1.5">
                    <title>{z.name}</title>
                  </rect>
                  <text x={nw.x + w / 2} y={nw.y + h / 2} fill="#0F172A" fontSize="11" fontWeight="600"
                        textAnchor="middle" dominantBaseline="middle" pointerEvents="none">
                    {z.name}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-3 max-h-[600px] overflow-y-auto">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-2 px-1">All {tab} zones</div>
          <ul className="space-y-1.5">
            {cityZones.map((z) => (
              <li key={z.id}>
                <button
                  onClick={() => setEdit({ zone: z, name: z.name, color: z.color, saving: false })}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-50 text-left"
                  data-testid={`zone-row-${z.id}`}
                >
                  <span className="h-4 w-4 rounded-md flex-shrink-0" style={{ backgroundColor: z.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{z.name}</div>
                    <div className="text-[10px] text-slate-400 font-mono truncate">{z.id}</div>
                  </div>
                  <Palette className="h-3.5 w-3.5 text-slate-300" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-md">
          {edit && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className="h-4 w-4 rounded-md" style={{ backgroundColor: edit.color }} />
                  Edit zone
                </DialogTitle>
                <DialogDescription>
                  Rename this delivery zone or pick a new color. Changes are visible on every driver + vendor map immediately.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-600">Zone name</Label>
                  <Input
                    value={edit.name}
                    onChange={(e) => setEdit((s) => ({ ...s, name: e.target.value }))}
                    placeholder="e.g. Centretown East"
                    data-testid="zone-edit-name"
                  />
                  <div className="mt-1 text-[10px] text-slate-400 font-mono">ID: {edit.zone.id}</div>
                </div>
                <div>
                  <Label className="text-xs text-slate-600 flex items-center gap-1.5">
                    <Palette className="h-3 w-3" /> Color
                  </Label>
                  <div className="grid grid-cols-10 gap-1.5 mt-1.5">
                    {COLOR_SWATCHES.map((c) => (
                      <button
                        key={c}
                        onClick={() => setEdit((s) => ({ ...s, color: c }))}
                        className={`h-7 w-7 rounded-md transition ${edit.color === c ? 'ring-2 ring-slate-900 ring-offset-2 scale-110' : 'hover:scale-110'}`}
                        style={{ backgroundColor: c }}
                        title={c}
                        aria-label={`Color ${c}`}
                        data-testid={`zone-color-swatch-${c.replace('#', '')}`}
                      />
                    ))}
                  </div>
                  <Input
                    className="mt-2 font-mono h-8 text-xs"
                    value={edit.color}
                    onChange={(e) => setEdit((s) => ({ ...s, color: e.target.value }))}
                    data-testid="zone-edit-color-hex"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
                <Button onClick={save} disabled={edit.saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="zone-edit-save">
                  {edit.saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                  Save changes
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ZonesAdminPage;
